import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { optionalInt, searchPatch, optionalSearch } from "@/lib/search";
import { AccessDenied } from "@/components/nancy/access-denied";
import { NancyCommunityChoice } from "@/routes/_authenticated/nancy/-lib/nancy-community-choice";
import { useNancyFacility } from "@/routes/_authenticated/nancy/-lib/use-nancy-facility";
import { OrdersHome } from "./-components/orders-home";

// Guarded by the /ws/nancy layout, like every screen under it.
export const Route = createFileRoute("/_authenticated/ws/nancy/")({
  validateSearch: (search: Record<string, unknown>): { q?: string; page?: number } => ({
    q: optionalSearch(search.q),
    page: optionalInt(search.page, 2, 100000),
  }),
  head: () => ({
    meta: [
      { title: "Orders | Nancy | People AI" },
      { name: "description", content: "Nancy's orders for this community." },
    ],
  }),
  component: NancyHome,
});

/** Nancy's home for one building: its own first page and rows, never the last building's. */
function NancyHome() {
  const { facilityId, can, loading } = useNancyFacility();
  const { q, page = 1 } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  if (!loading && !facilityId) return <NancyCommunityChoice />;
  if (facilityId && !can("read")) {
    return (
      <AccessDenied
        title="No access to Nancy"
        description="You do not have access to Nancy's orders in this community."
      />
    );
  }
  return (
    <OrdersHome
      key={facilityId}
      q={q}
      page={page - 1}
      onSearch={(next) => patch({ q: next, page: undefined })}
      onPage={(next) => patch({ page: next > 0 ? next + 1 : undefined })}
    />
  );
}
